<template>
	<div id="qvo-app">
		<titlebar />
		<notificationCenter />
		<navbar />
		<main id="content" class="configuration-explorer">
			<div class="configuration-explorer-labels">
				<h1>Configuration Explorer</h1>

				<template
					v-if="
						profileToEditLabel !== '' ||
						titleID === 'select-profile'
					"
				>
					<h3 class="configuration-explorer-labels-profile" v-show="titleID !== 'select-profile'">
						{{ profileToEditLabel }}
					</h3>
				</template>
				<template v-else>
					<v-skeleton-loader type="chip"></v-skeleton-loader>
				</template>
			</div>
			<back-nav-btn :action="checkUnsavedOnExit">
				Exit Explorer
			</back-nav-btn>

			<screenFlow :value="titleID" ref="screenFlow">
				<screenFlowStep
					title="Select Profile"
					:index="0"
					:showFlowActionForward="false"
				>
					<selectProfile @onReconfigure="onReconfigure" />
				</screenFlowStep>
				<screenFlowStep
					title="Set Configuration"
					:index="1"
					nextText="Next | Set Decisions"
					prevText="Back | Select Profile"
					:prevAction="backToSelectProfile"
					:nextAction="goingToSetDecisionMatrix"
					:showFlowActionForward="allowSetConfigProgress"
					:showFlowActionBack="allowSetConfigProgress"
					:validate="validateConfig"
					:showPrevDialog="hasProfileChanged"
					:prevDialog="prevDialog"
				>
					<setConfiguration ref="setConfiguration"/>
				</screenFlowStep>
				<screenFlowStep
					title="Set Decisions"
					:index="2"
					nextText="Next | Review & Evaluate"
					prevText="Back | Set Configuration"
					:prevAction="backToSetConfiguration"
					:nextAction="goingToReviewAndEvaluate"
					:validate="validateSetDecisionMatrix"
				>
					<setDecisionMatrix ref="setDecisionMatrix"/>
				</screenFlowStep>
				<screenFlowStep
					title="Review & Evaluate"
					:index="3"
					nextText="Save & Activate"
					prevText="Back | Set Decisions"
					:nextAction="onSaveAndActivate"
					:prevAction="backToSetDecisionMatrix"
					:showFlowActionForward="showSaveForReview"
					:showFlowActionBack="showBackForReview"
				>
					<reviewAndEvaluate @deploy="onDeploy"/>
				</screenFlowStep>
				<div slot="additional-actions" v-if="showAdditionalActions">
					<v-btn
						v-if="hasProfileChanged && configurationValid && thresholdFormValid && initialDecisionsValid"
						class="secondary additional-actions-save"
						@click="onSave"
						:loading="saveInProgress"
					>
						Save Changes
					</v-btn>
					<v-btn
						v-else
						text
						disabled
						class="additional-actions-save"
					>
						Profile Up-to-Date
					</v-btn>
					<v-btn
						v-if="titleID === 'review-&-evaluate'"
						text
						class="additional-actions-save"
						@click="checkUnsavedOnExit"
					>
						Close Profile
					</v-btn>
					<v-btn
						v-if="titleID === 'review-&-evaluate'"
						text
						class="additional-actions-save"
						@click="onSaveAndActivate"
					>
						Save & Activate
					</v-btn>
				</div>
			</screenFlow>
			<modal-dialog
				title="Activate Profile"
				executeText="Activate Profile"
				:executeAction="onDeploy"
				:triggerCounter="activateProfileDialogCounter"
				:useActivator="false"
			>
				<div slot="content">
					<v-alert
						class="warning"
						border="left"
						colored-border
						icon="mdi-alert-rhombus"
					>
						Activation will replace the current profile, effective
						immediately.
					</v-alert>
				</div>
			</modal-dialog>
			<modal-dialog
				title="Unsaved Changes"
				executeText="Discard Changes"
				:executeAction="cancelAndExit"
				:triggerCounter="unsavedChangesDialogCounter"
				:useActivator="false"
			>
				<div slot="content">
					<unsavedChangesDialogContent />
				</div>
			</modal-dialog>
		</main>
		<footnote />
	</div>
</template>

<script>
	/**
	 * @fileoverview configuration-explorer.vue allows the users to navigate through the four stages of the configuration
	 * explorer. It relies on vuex to map the state variables from the store files.
	 * 
	 * @components
	 * 		selectProfile - data table displaying the available profiles for the active tenant,
	 *		screenFlow - navigation component,
	 *		screenFlowStep - individual step within the navigation component,
	 *		unsavedChangesDialogContent - unsaved changes dialog box content,
	 *		setConfiguration - Set Configuration step of the configuration explorer,
	 *		setDecisionMatrix - Set Decisions step of the configuration explorer,
	 *		reviewAndEvaluate - Review & Evaluate step of the configuration explorer
	 * 
	 * @author Sophia Frankel <sophia.frankel@quavo.com>
	 */

	import selectProfile from "@/components/configuration-explorer/select-profile";
	import screenFlow from "@/qux-common/components/screen-flow.vue";
	import screenFlowStep from "@/qux-common/components/screen-flow-step.vue";
	import unsavedChangesDialogContent from "@/components/configuration-explorer/unsaved-changes-dialog-content.vue";
	import { mapState } from "vuex";

	export default {
		name: "configurationExplorer",
		data() {
			return {
				deployInProgress: false,	// Deploying profile in progress
				saveInProgress: false,	// Saving profile in progress
				activateProfileDialogCounter: 0,	// Incremented to open the activate profile dialog box
				unsavedChangesDialogCounter: 0,		// Incremented to open the unsaved changes dialog box
				/**
				 * Unsaved Changes dialog object
				*/
				prevDialog: {
					title: "Unsaved Changes",
					executeText: "Discard Changes",
					content: unsavedChangesDialogContent,
				},
			};
		},
		computed: {
			...mapState({
				configProfile: (state) => state.config.configProfile,
				allowSetConfigProgress: (state) =>
					state.config.editingTreeDepth === 0,
				allowProfileDeploy: (state) => state.config.allowProfileDeploy,
				configurationValid: (state) => state.config.configurationValid,
				thresholdFormValid: (state) => state.config.thresholdFormValid,
				initialDecisionsValid: (state) =>
					state.config.initialDecisionsValid,
				hasProfileChanged: (state) => state.config.hasProfileChanged,
				activeTenantId: (state) => state.account.activeTenantId,
				profileToEditLabel: (state) => state.config.profileToEditLabel,
				challengerProfile: (state) => state.config.challengerProfile,
				challengerSuccessful: (state) => state.config.challengerSuccessful,
				challengerStatusTimer: (state) => state.config.challengerStatusTimer
				
			}),
			/**
			 * Show additional acions on all steps except 'select-profile'
			 * @returns true if not on the 'select-profile' step
			 */
			showAdditionalActions() {
				let show = true;
				if (this.titleID === 'select-profile') {
					show = false;
				}
				return show;
			},
			/**
			 * Show save button
			 * @returns true if the CC was successful
			 */
			showSaveForReview() {
				let show = this.challengerSuccessful === undefined ? false : this.challengerSuccessful;
				return show;
			},
			/**
			 * Show back button
			 * @returns true if a profile is not currently being deployed
			 */
			showBackForReview() {
				let show = !this.deployInProgress;
				return show;
			},
			/**
			 * Get the title id for the step
			 * @return string of the step title id
			 */
			titleID() {
				// get step Id from URL
				let step = this.$route.params.step;

				if (step === undefined) {
					return "select-profile";
				}

				return step;
			},
		},
		/**
		 * Created lifecycle hook
		 * Retrieve the overall config for the active tenant
		 */
		created() {
			let payload = {
				tenantId: this.activeTenantId,
				route: this.$route
			};

			this.$store.dispatch("retrieveOverallConfig", payload);
		},
		watch: {
			/**
			* Set saveInProgress to false when hasProfileChanged is set to false
			*/
			hasProfileChanged: function() {
				if (this.hasProfileChanged === false) {
					// if everything is up-to-date, then we clearly aren't waiting on a save :)
					this.saveInProgress = false;
				}
			},
			/**
			 * Set the saveInProgress flag to false after 5 seconds
			 */
			saveInProgress() {
				if (this.saveInProgress) {
					this.timeout = setTimeout(() => (this.saveInProgress = false), 5000);
				}
			}
		},
		components: {
			selectProfile,
			screenFlow,
			screenFlowStep,
			unsavedChangesDialogContent,
			setConfiguration: () =>
				import("@/components/configuration-explorer/set-configuration"), // lazy load
			setDecisionMatrix: () =>
				import("@/components/configuration-explorer/set-decision-matrix"), // lazy load
			reviewAndEvaluate: () =>
				import("@/components/configuration-explorer/review-and-evaluate"), // lazy load
		},
		methods: {
			/**
			* Reset configuration data
			* @param {?Object} empty - Clear everything. Defaults to true
			*/
			resetData(emptyConfig = true) {
				// Clean up any condition-driving data to avoid artifacts
				this.$store.dispatch("setAllowProfileDeploy", false);
				this.$store.dispatch("setEditingTreeDepth", 0);
				if (emptyConfig) {
					this.$store.dispatch("clearFactorConfigurations");
					this.$store.dispatch("clearProfilePerformance");
					this.$store.dispatch("clearChallengerData");
					// clear auditMessages
					this.$store.dispatch("clearProfileAuditMessages");
					clearInterval(this.challengerStatusTimer);
				}

				this.deployInProgress = false;
			},
			/**
			* Re-configure profile button. Retrieves the selected profile and moves the user to the set configuration step.
			* @param {Object} profile - Profile object used to retrieve the selected profile data
			*/
			async onReconfigure(profile) {
				var payload = {
					profileId: String(profile.profileId),
					tenantId: this.activeTenantId,
				};
				
				await this.resetData();
				await this.$store.dispatch("setProfileToEditLabel", profile.profileLabel);
				await this.$store.dispatch("retrieveProfile", payload);

				this.$router.push({
					name: "Configuration Explorer",
					params: { step: "set-configuration", id: profile.profileId },
				});
			},
			/**
			* Open the unsaved changes dialog box if the profile has changed, otherwise proceed
			*/
			checkUnsavedOnExit() {
				// either trigger the dialog or immediately exit
				// only relevant for Cancel & Exit
				if (this.hasProfileChanged) {
					this.unsavedChangesDialogCounter += 1;
				} else {
					this.cancelAndExit();
				}
			},
			/**
			* Return to the select profile step. Reset the data
			*/
			backToSelectProfile() {
				this.$router.push({
					name: "Configuration Explorer",
					params: { step: "select-profile" },
				});
				this.$store.dispatch("setProfileChanged", false);
				this.resetData();
			},
			/**
			* Return to the set configuration step. Reset some of the data
			*/
			backToSetConfiguration() {
				this.$router.push({
					name: "Configuration Explorer",
					params: {
						step: "set-configuration",
						id: this.$route.params.id,
					},
				});

				this.resetData(false);
			},
			/**
			* Move to the set decisions step
			*/
			goingToSetDecisionMatrix() {
				let moveForward = function(app) {
					app.$router.push({
						name: "Configuration Explorer",
						params: {
							step: "set-decisions",
							id: app.$route.params.id,
						},
					});
				}

				if (this.hasProfileChanged) {
					this.saveInProgress = false;
					this.$refs.setConfiguration.onSave().then(() => {
						this.saveInProgress = false;
						moveForward(this);
					})
					.catch(() => {
						this.saveInProgress = false;
					});
				} else {
					moveForward(this);
				}
			},
			/**
			* Return to the set decisions step. Reset some of the data
			*/
			backToSetDecisionMatrix() {
				this.$router.push({
					name: "Configuration Explorer",
					params: {
						step: "set-decisions",
						id: this.$route.params.id,
					},
				});

				this.resetData(false);
			},
			/**
			* Move to the review and evaluate step
			*/
			goingToReviewAndEvaluate() {
				let moveForward = function(app) {
					app.saveInProgress = false;
					app.$router.push({
						name: "Configuration Explorer",
						params: {
							step: "review-&-evaluate",
							id: app.$route.params.id,
						},
					});
				}

				if (this.hasProfileChanged) {
					this.saveInProgress = true;
					this.$refs.setDecisionMatrix.onSave().then(() => {
						moveForward(this);
					})
					.catch(() => {
						this.saveInProgress = false;
					});
				} else {
					moveForward(this);
				}
			},
			/**
			* Return to the configuration landing page. Reset the data
			*/
			cancelAndExit() {
				this.$router.push({ name: "Configuration" });
				this.$store.dispatch("setProfileChanged", false);
				this.resetData();
			},
			/**
			* Save the profile
			*/
			async onSave() {
				this.saveInProgress = true;
				if (this.titleID === 'review-&-evaluate') {
					this.$refs.setDecisionMatrix.onSave(false).then(async () => {
						await this.$ref.setConfiguration.onSave();
					})
				} else if (this.titleID === 'set-configuration') {
					await this.$refs.setConfiguration.onSave();
				} else if (this.titleID === 'set-decisions') {
					await this.$refs.setDecisionMatrix.onSave();
				}

				// clear auditMessages
				this.$store.dispatch("clearProfileAuditMessages");

			},
			/**
			* Increment the save and activate dialog counter
			*/
			onSaveAndActivate() {
				this.activateProfileDialogCounter += 1;
			},
			/**
			* Deploy the profile. Reset the data
			*/
			onDeploy() {
				this.deployInProgress = true;
				let deployFunction = "deployProfile";
				
				if (this.activeTenantId === 'master') {
					deployFunction = "deployDefaultMasterProfile";
				}

				this.$store.dispatch(deployFunction, this.$router).then(() => {
					this.resetData();
				})
				.catch((error) => {
					console.error("error caught in onDeploy:" + error);
					this.deployInProgress = false;
				});
			},
			/**
			* Validate the set configuration page
			* @returns resolve or reject the validation
			*/
			validateConfig() {
				return new Promise((resolve, reject) => {
					let verdict = false;

					// Are the buttons available to move on?
					verdict = this.allowSetConfigProgress;
					// Are the threshold forms valid?
					verdict = (this.thresholdFormValid && this.configurationValid) ? verdict : false;

					if (verdict) {
						resolve();
					} else {
						reject("Fix missing or invalid settings to continue.");
					}
				});
			},
			/**
			* Validate the set decisions page
			* @returns resolve or reject the validation
			*/
			validateSetDecisionMatrix() {
				return new Promise((resolve, reject) => {
					let verdict = true;

					// Are the initial decision values valid?
					verdict = this.initialDecisionsValid ? verdict : false;

					if (verdict) {
						resolve();
					} else {
						reject("Fix missing or invalid settings to continue.");
					}
				});
			},
		},
	};
</script>

<!-- Page-Specific Style Import (don't edit here) -->
<style lang="scss">
	@import "@/design/views/configuration-explorer.scss";
</style>